import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { act, useFetch } from "@/lib/hooks";
import { refreshBranding } from "@/components/Brand";
import { AddressMap } from "@/components/AddressMap";
import { Field, Loading, PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const KIT = [
  ["nms-premium.svg", "Complète · métal sur noir", "bg-[#050505]"],
  ["nms-flat-black.svg", "Plate noire · PDF & impression", "bg-white"],
  ["nms-flat-white.svg", "Plate blanche · fonds sombres", "bg-[#1a1a1a]"],
  ["nms-monogram.svg", "Monogramme NMS", "bg-[#050505]"],
  ["nms-icon.svg", "Icône app (favicon, PWA)", "bg-[#121212]"],
];

const FIELDS: [string, string][] = [
  ["name", "Nom de la société (raison sociale)"], ["trade_name", "Nom du magasin"], ["activity", "Activité (en-tête)"], ["legal_form", "Forme juridique"], ["capital", "Capital social"], ["address", "Adresse"],
  ["phone", "Téléphone"], ["email", "Email"], ["nif", "NIF"], ["rc", "RC"], ["nis", "NIS"], ["art_imp", "Article d'imposition"], ["rib", "RIB"],
];

function LogoSlot({ slot, title, current, onSaved }: { slot: string; title: string; current: string | null; onSaved: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [err, setErr] = useState("");
  const pick = (f?: File) => {
    setErr("");
    if (!f) return;
    if (!/\.(svg|png)$/i.test(f.name)) return setErr("Formats acceptés : SVG ou PNG");
    if (f.size > 2 * 1024 * 1024) return setErr("Fichier trop lourd (2 Mo maximum)");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const upload = async () => {
    const fd = new FormData();
    fd.append("slot", slot);
    fd.append("file", file as File);
    try {
      await api.post("/settings/logo", fd);
      toast.success("Nouveau logo en place. Les documents déjà validés gardent l'ancien.");
      setFile(null);
      setPreview("");
      refreshBranding();
      onSaved();
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  const bg = slot === "print" ? "bg-white" : "bg-[#050505]";
  return (
    <div className="rounded-sm border border-border p-4" data-testid={`logo-slot-${slot}`}>
      <div className="eyebrow mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-3">
        <div><div className="mb-1 text-xs text-muted-foreground">Actuel</div><div className={`flex h-24 items-center justify-center rounded-sm p-3 ${bg}`}>{current ? <img src={current} alt="" className="max-h-full" /> : <img src="/brand/nms-flat-white.svg" alt="" className="max-h-full" />}</div></div>
        <div><div className="mb-1 text-xs text-muted-foreground">Aperçu</div><div className={`flex h-24 items-center justify-center rounded-sm border border-dashed border-border p-3 ${preview ? bg : ""}`} data-testid={`logo-preview-${slot}`}>{preview ? <img src={preview} alt="" className="max-h-full" /> : <span className="text-xs text-muted-foreground">—</span>}</div></div>
      </div>
      {err && <div className="mt-3 text-sm text-red-400" data-testid={`logo-error-${slot}`}>{err}</div>}
      <div className="mt-3 flex gap-2">
        <Button variant="outline" onClick={() => input.current?.click()} data-testid={`logo-pick-${slot}`}><Upload className="mr-2 h-4 w-4" />Choisir SVG / PNG</Button>
        {file && <Button onClick={upload} data-testid={`logo-confirm-${slot}`}>Valider ce logo</Button>}
      </div>
      <input ref={input} type="file" accept=".svg,.png,image/svg+xml,image/png" hidden onChange={(e) => pick(e.target.files?.[0])} data-testid={`logo-input-${slot}`} />
    </div>
  );
}

function ResetPanel() {
  const [scope, setScope] = useState("factures");
  const [confirm, setConfirm] = useState("");
  const run = async () => {
    if (await act(() => api.post("/admin/reset", { scope, confirm }), scope === "tout" ? "Toutes les données ont été remises à zéro" : "Factures et encaissements remis à zéro")) setConfirm("");
  };
  const opts: [string, string, string][] = [
    ["factures", "Factures seulement", "factures, bons de paiement, avoirs, encaissements, reçus ; la numérotation FAC / BP / AV / REC repart à 00001"],
    ["tout", "Tout", "clients, véhicules, RDV, réceptions, devis, OR, factures, encaissements, stock et journal ; on garde les comptes, paramètres, logo et catalogue"],
  ];
  return (
    <Panel title="Remise à zéro (avant mise en service)" className="mt-6 border-red-500/50" testId="reset-panel">
      <div className="space-y-3 text-sm">
        {opts.map(([k, l, d]) => (
          <label key={k} className="flex cursor-pointer items-start gap-3"><input type="radio" name="reset-scope" checked={scope === k} onChange={() => setScope(k)} className="mt-1" data-testid={`reset-scope-${k}`} />
            <span><b>{l}</b><span className="block text-xs text-muted-foreground">{d}</span></span></label>
        ))}
        <p className="flex items-center gap-2 text-red-400"><AlertTriangle className="h-4 w-4" />Action définitive : les données effacées ne peuvent pas être récupérées.</p>
        <div className="flex flex-wrap gap-2">
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Tapez REMISE A ZERO" className="h-11 max-w-xs" data-testid="reset-confirm-input" />
          <Button variant="destructive" className="h-11" disabled={confirm.trim().toUpperCase() !== "REMISE A ZERO"} onClick={run} data-testid="reset-submit-btn">Remettre à zéro</Button>
        </div>
      </div>
    </Panel>
  );
}

export default function Settings() {
  const { data, reload } = useFetch<any>("/settings");
  const [f, setF] = useState<any>(null);
  useEffect(() => {
    if (data) setF(data);
  }, [data]);
  if (!f) return <Loading />;
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const save = () => act(() => api.put("/settings", { ...f, tva_rate: Number(f.tva_rate), inactivity_minutes: Number(f.inactivity_minutes), quote_validity_days: Number(f.quote_validity_days) }), "Paramètres enregistrés").then((r) => r && reload());
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Paramètres du garage" actions={<Button className="h-11" onClick={save} data-testid="settings-save-btn">Enregistrer</Button>} />
      <div className="mb-6 flex gap-3 rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200 [.light_&]:text-amber-800" data-testid="legal-warning">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        Le modèle de facture, les mentions légales et le taux de TVA doivent être validés par votre comptable avant la mise en service (décret exécutif 05-468). L'application ne garantit pas seule la conformité fiscale.
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Identité légale (figée sur chaque document validé)">
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, l]) => <Field key={k} label={l}><Input value={f[k] || ""} onChange={set(k)} data-testid={`settings-${k}-input`} /></Field>)}
            <Field label="Mentions légales facture" className="sm:col-span-2"><Textarea rows={3} value={f.invoice_mentions || ""} onChange={set("invoice_mentions")} data-testid="settings-mentions-input" /></Field>
            <div className="sm:col-span-2"><div className="eyebrow mb-2">Plan d'accès</div><AddressMap address={data.address} /></div>
          </div>
        </Panel>
        <div className="space-y-6">
          <Panel title="Fiscalité & sécurité">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="TVA par défaut (%)"><Input type="number" value={f.tva_rate} onChange={set("tva_rate")} data-testid="settings-tva-input" /></Field>
              <Field label="Inactivité (min)"><Input type="number" value={f.inactivity_minutes} onChange={set("inactivity_minutes")} data-testid="settings-inactivity-input" /></Field>
              <Field label="Validité devis (j)"><Input type="number" value={f.quote_validity_days} onChange={set("quote_validity_days")} data-testid="settings-validity-input" /></Field>
            </div>
          </Panel>
          <Panel title="Logo (gérant uniquement · SVG ou PNG · 2 Mo max)">
            <div className="space-y-4">
              <LogoSlot slot="print" title={`Logo des PDF · version ${f.logo_print_version} · plat noir recommandé`} current={f.logo_print} onSaved={reload} />
              <LogoSlot slot="screen" title={`Logo écran (connexion, barre latérale) · version ${f.logo_screen_version}`} current={f.logo_screen} onSaved={reload} />
            </div>
          </Panel>
        </div>
        <Panel title="Kit logo NMS" className="xl:col-span-2" testId="brand-kit">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {KIT.map(([file, label, bg]) => (
              <a key={file} href={`/brand/${file}`} download className="group rounded-sm border border-border transition-colors hover:border-primary/60" data-testid={`kit-${file}`}>
                <div className={`flex h-28 items-center justify-center p-4 ${bg}`}><img src={`/brand/${file}`} alt={label} className="max-h-full max-w-full" /></div>
                <div className="flex items-center justify-between p-3 text-xs"><span>{label}</span><Download className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" /></div>
              </a>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Icônes générées depuis le monogramme : favicon 16/32/48 px, PWA 192/512 px, apple-touch-icon 180 px. Version provisoire : remplacez par le fichier vectoriel du designer dès qu'il est disponible.</p>
        </Panel>
      </div>
      <ResetPanel />
    </div>
  );
}
